import {
	type PromptTemplateId,
	templateShape,
} from "@pluck/api/lib/board/compose";
import type {
	AspectRatio,
	QualityTier,
} from "@pluck/api/lib/board/prompt-plan";
import type { AppRouter } from "@pluck/api/routers/index";
import type { inferRouterOutputs } from "@trpc/server";

export type HostedModelInfo =
	inferRouterOutputs<AppRouter>["generations"]["models"][number];

const TEXT_HEAVY_RE =
	/\b(text|copy|headline|heading|label|button|pricing|price|menu|nav|form|dashboard|table|landing|page|ui|screen)\b/i;

/**
 * Mirrors the server's Auto pick (packages/api/src/lib/hosted/models.ts) so
 * the cost can be shown before running. The server decides what's charged.
 */
export function pickAutoModel(
	models: HostedModelInfo[],
	prompt: string,
	referenceCount: number
): HostedModelInfo | undefined {
	const candidates = models.filter((m) => m.maxReferences >= referenceCount);
	const wantsText = TEXT_HEAVY_RE.test(prompt) || referenceCount > 0;
	const ranked = candidates
		.filter((m) => m.tier === "standard" && (!wantsText || m.goodAtText))
		.concat(candidates.filter((m) => m.tier === "standard"))
		.concat(candidates);
	return ranked[0];
}

const TIER_ORDER: QualityTier[] = ["draft", "standard", "high", "max"];

export const TIER_LABELS: Record<QualityTier, string> = {
	draft: "Draft",
	standard: "Standard",
	high: "High",
	max: "Max",
};

/** The aspect an "auto" prompt runs at under the account's template. */
export function templateAspect(template: PromptTemplateId): AspectRatio {
	const shape = templateShape(template);
	if (shape === "landscape") {
		return "16:9";
	}
	return shape === "portrait" ? "9:16" : "auto";
}

/** Mirrors the server's quality fallback (packages/api/src/lib/hosted/models.ts). */
export function resolveModelQuality(
	model: HostedModelInfo,
	requested: QualityTier | undefined
): QualityTier {
	const wanted = requested ?? model.defaultQuality;
	if (model.qualities.some((q) => q.id === wanted)) {
		return wanted;
	}
	const rank = TIER_ORDER.indexOf(wanted);
	let best = model.qualities[0]?.id ?? model.defaultQuality;
	for (const option of model.qualities) {
		if (TIER_ORDER.indexOf(option.id) <= rank) {
			best = option.id;
		}
	}
	return best;
}

/** Credits for one run of a model with a prompt's settings. */
export function runCredits(
	model: HostedModelInfo,
	settings: { aspect?: AspectRatio; quality?: QualityTier },
	template: PromptTemplateId
): number {
	const aspect =
		settings.aspect && settings.aspect !== "auto"
			? settings.aspect
			: templateAspect(template);
	const quality = resolveModelQuality(model, settings.quality);
	return model.prices[quality]?.[aspect] ?? model.credits;
}
